import React, { useCallback, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";

import { differenceInHours, isBefore, startOfTomorrow } from "date-fns/esm";
import { useIntl } from "react-intl";

import { Activity } from "@mapmycustomers/shared/types/entity";
import { Button } from "@mapmycustomers/ui";

import AnnotationEditModal from "@app/component/activity/ActivityAnnotation/AnnotationEditModal";
import { showEntityView } from "@app/store/entityView/actions";
import { getPendingActivity, isPendingActivityUpdate } from "@app/store/frequency";
import { donePendingActivity } from "@app/store/frequency/actions";
import { RootState } from "@app/store/rootReducer";
import { ACTIVITY_TIME_DIFFERENCE_ALERT } from "@app/util/activity/const";
import showActivityTimeUpdateAlert from "@app/util/activity/showActivityTimeUpdateAlert";
import { formatDateRelativeExtended } from "@app/util/formatters";
import { activityLayoutModel } from "@app/util/layout/impl";
import { parseApiDate } from "@app/util/parsers";

import styles from "./PendingActivityState.module.scss";

interface Props {
  donePendingActivity: typeof donePendingActivity.request;
  onEdit: typeof showEntityView;
  pendingActivity?: Activity;
  updating: boolean;
}

const PendingActivityState: React.FC<Props> = ({
  donePendingActivity,
  onEdit,
  pendingActivity,
  updating,
}) => {
  const intl = useIntl();
  const ref = useRef<HTMLElement>(null);
  const [previewActivityId, setPreviewActivityId] = useState<Activity["id"] | undefined>();

  const startAt = useMemo(
    () => (pendingActivity?.startAt ? parseApiDate(pendingActivity.startAt) : undefined),
    [pendingActivity?.startAt]
  );

  const endAt = useMemo(
    () => (pendingActivity?.endAt ? parseApiDate(pendingActivity.endAt) : undefined),
    [pendingActivity?.endAt]
  );

  const handlePreview = useCallback(() => {
    if (pendingActivity) {
      setPreviewActivityId(pendingActivity.id);
    }
  }, [pendingActivity, setPreviewActivityId]);

  const hasMissingRequiredFields = useMemo(() => {
    return pendingActivity
      ? activityLayoutModel.hasMissingRequiredFieldsFor(pendingActivity)
      : false;
  }, [pendingActivity]);

  const handleMarkDone = useCallback(() => {
    if (pendingActivity && !pendingActivity.completed && hasMissingRequiredFields) {
      onEdit({
        edit: true,
        editValues: { completed: true },
        entityId: pendingActivity.id,
        entityType: pendingActivity.entity,
        requiredFieldsOnly: true,
      });
    } else {
      if (
        pendingActivity &&
        startAt &&
        Math.abs(differenceInHours(Date.now(), endAt ?? startAt)) >= ACTIVITY_TIME_DIFFERENCE_ALERT
      ) {
        showActivityTimeUpdateAlert(intl, pendingActivity, donePendingActivity);
      } else {
        donePendingActivity(pendingActivity);
      }
    }
  }, [
    donePendingActivity,
    endAt,
    hasMissingRequiredFields,
    intl,
    onEdit,
    pendingActivity,
    startAt,
  ]);

  if (!startAt) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        {intl.formatMessage(
          {
            id: "frequency.pendingActivity.title",
            defaultMessage: "Pending activity {date}",
            description: "frequency component - pending activity - title",
          },
          {
            date: formatDateRelativeExtended(startAt, Date.now(), {
              useTime: false,
            }),
          }
        )}
      </div>

      {isBefore(startAt, startOfTomorrow()) ? (
        <Button
          disabled={updating}
          loading={updating}
          onClick={handleMarkDone}
          size="small"
          type="primary"
        >
          {intl.formatMessage({
            id: "frequency.pendingActivity.done",
            defaultMessage: "Mark Done",
            description: "frequency component - pending activity - Mark Done",
          })}
        </Button>
      ) : (
        <Button onClick={handlePreview} ref={ref} size="small">
          {intl.formatMessage({
            id: "frequency.pendingActivity.review",
            defaultMessage: "Review Activity",
            description: "frequency component - pending activity - Review Activity",
          })}
        </Button>
      )}
      {previewActivityId && ref.current && (
        <AnnotationEditModal
          activityId={previewActivityId}
          element={ref.current}
          onHide={() => setPreviewActivityId(undefined)}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  pendingActivity: getPendingActivity(state),
  updating: isPendingActivityUpdate(state),
});

const mapDispatchToProps = {
  donePendingActivity: donePendingActivity.request,
  onEdit: showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(PendingActivityState);
